import { QueryClientProvider } from "@tanstack/react-query";
import { Link, RouterProvider, createRouter } from "@tanstack/react-router";
import { ThemeProvider } from "next-themes";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";

import { AuthProvider, useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { queryClient } from "@/lib/queryClient";

import "../index.css";

import { routeTree } from "./routeTree.gen";

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFoundComponent,
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  context: {
    auth: undefined!,
    queryClient,
  },
});

function InnerApp() {
  const auth = useAuth();
  if (!auth.initialized) return null;
  return <RouterProvider router={router} context={{ auth, queryClient }} />;
}

function NotFoundComponent() {
  return (
    <div className="mx-auto flex max-w-xl flex-col items-center justify-center px-4 py-24 text-center space-y-4">
      <p className="text-7xl font-bold tracking-tight bg-linear-to-r from-foreground to-cyan-500 inline-block text-transparent bg-clip-text">
        404
      </p>
      <h1 className="text-2xl font-semibold tracking-tight">
        Sidan kunde inte hittas
      </h1>
      <p className="text-muted-foreground">
        Länken kan vara felaktig, eller så har sidan flyttats.
      </p>
      <Button asChild variant="cta">
        <Link to="/">Tillbaka till startsidan</Link>
      </Button>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light">
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <InnerApp />
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <App />
    </StrictMode>
  );
}
